// API functions for astronomical image operations
import apiClient from './index';

// Shared function to process stamp response
const processStampResponse = (response, format) => {
  // Extract metadata from headers
  const metadataHeader = response.headers['x-metadata'];
  let metadata = {};
  
  // Extract coordinates and other info from headers
  const headerFields = [
    { key: 'x-object-name', destKey: 'objectName' },
    { key: 'x-ra', destKey: 'ra', transform: parseFloat },
    { key: 'x-dec', destKey: 'dec', transform: parseFloat },
    { key: 'x-size', destKey: 'size', transform: parseFloat },
    { key: 'x-size-unit', destKey: 'sizeUnit' },
    { key: 'x-filter', destKey: 'filter' },
    { key: 'x-format', destKey: 'format' }
  ];
  
  // Process all header fields
  headerFields.forEach(field => {
    const headerValue = response.headers[field.key];
    if (headerValue) {
      metadata[field.destKey] = field.transform ? field.transform(headerValue) : headerValue;
    }
  });
  
  // Process detailed metadata if present
  if (metadataHeader) {
    try {
      // Convert Python-style values to JavaScript values
      const processedMetadata = metadataHeader
        .replace(/\(/g, '[')
        .replace(/\)/g, ']')
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null');
      
      // Combine with existing metadata
      metadata = { ...JSON.parse(processedMetadata), ...metadata };
    } catch (e) {
      console.error('Error parsing stamp metadata:', e);
    }
  }
  
  // Handle the response data based on format
  let data = response.data;
  if (format === "png") {
    // For PNG images, create a Blob with the correct MIME type
    data = new Blob([data], { type: 'image/png' });
  }
  
  return {
    data,
    contentType: response.headers['content-type'],
    filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'stamp.fits',
    metadata
  };
};

// Image collections
export const getImageCollections = async () => {
  const response = await apiClient.get('/adss/v1/images/collections/');
  return response.data;
};

export const getImageCollection = async (collectionId) => {
  const response = await apiClient.get(`/adss/v1/images/collections/${collectionId}`);
  return response.data;
};

export const createImageCollection = async (collectionData, patterns = null) => {
  const requestData = { ...collectionData };
  if (patterns !== null) {
    requestData.patterns = patterns;
  }

  console.log(patterns, requestData);
  const response = await apiClient.post('/adss/v1/images/collections/', requestData);
  return response.data;
};

export const updateImageCollection = async (collectionId, collectionData, patterns = null) => {
  const requestData = { ...collectionData };
  
  if (patterns !== null) {
    requestData.patterns = patterns;
  }
  
  const response = await apiClient.put(`/adss/v1/images/collections/${collectionId}`, requestData);
  return response.data;
};

export const deleteImageCollection = async (collectionId) => {
  const response = await apiClient.delete(`/adss/v1/images/collections/${collectionId}`);
  return response.data;
};

// Image files
export const getImageFiles = async (collectionId, options = {}) => {
  const { 
    skip = 0, 
    limit = 100, 
    filter, 
    filter_str, 
    objectName,
    ra_center,
    dec_center,
    radius,
    ra_min,
    ra_max,
    dec_min,
    dec_max,
    obsdate_min,
    obsdate_max
  } = options;
  
  let url = `/adss/v1/images/collections/${collectionId}/files?skip=${skip}&limit=${limit}`;
  if (filter) url += `&filter_name=${filter}`;
  if (filter_str) url += `&filter_str=${encodeURIComponent(filter_str)}`;
  if (objectName) url += `&object_name=${encodeURIComponent(objectName)}`;
  
  // Add cone search parameters
  if (ra_center !== undefined && dec_center !== undefined && radius !== undefined) {
    url += `&ra=${ra_center}&dec=${dec_center}&radius=${radius}`;
  }
  
  // Add box search parameters
  if (ra_min !== undefined && ra_max !== undefined) {
    url += `&ra_min=${ra_min}&ra_max=${ra_max}`;
  }
  
  if (dec_min !== undefined && dec_max !== undefined) {
    url += `&dec_min=${dec_min}&dec_max=${dec_max}`;
  }
  
  // Add observation date parameters
  if (obsdate_min) {
    url += `&obsdate_min=${encodeURIComponent(obsdate_min)}`;
  }
  
  if (obsdate_max) {
    url += `&obsdate_max=${encodeURIComponent(obsdate_max)}`;
  }
  
  const response = await apiClient.get(url);
  return response.data;
};

// Scanning
export const scanDirectory = async (collectionId, rescanExisting = false) => {
  const response = await apiClient.post(`/adss/v1/images/collections/${collectionId}/scan`, {
    collection_id: collectionId,
    rescan_existing: rescanExisting
  });
  return response.data;
};

export const getScanJobStatus = async (jobId) => {
  const response = await apiClient.get(`/adss/v1/images/scan-jobs/${jobId}`);
  return response.data;
};

// Image operations
export const createStamp = async (fileId, ra, dec, size, sizeUnit = 'arcmin', format = 'fits', zmin = null, zmax = null) => {
  // Create stamp by file ID
  const response = await apiClient.post(`/adss/v1/images/files/${fileId}/stamp`, 
    { ra, dec, size, size_unit: sizeUnit, format, zmin, zmax },
    { responseType: format === 'fits' ? 'blob' : 'arraybuffer' }
  );
  
  return processStampResponse(response, format);
};

export const createStampByObject = async (
  collectionId,
  objectName,
  filterName,
  size,
  format = 'fits',
  sizeUnit = 'arcmin',
  ra,
  dec,
  zmin = null,
  zmax = null,
  pattern = null
) => {
  const response = await apiClient.post(
    `/adss/v1/images/collections/${collectionId}/stamp_by_object`,
    {
      object_name: objectName,
      filter_name: filterName,
      size,
      size_unit: sizeUnit,
      format,
      ra,
      dec,
      zmin,
      zmax,
      pattern
    },
    { responseType: format === "fits" ? 'blob' : 'arraybuffer' }
  );
  
  return processStampResponse(response, format);
};

export const createStampByCoordinates = async (
  collectionId, 
  ra, 
  dec, 
  size, 
  filter, 
  format = "fits", 
  sizeUnit = 'arcmin', 
  zmin = null, 
  zmax = null,
  pattern = null
) => {
  // Create stamp by coordinates and filter
  const response = await apiClient.post(`/adss/v1/images/collections/${collectionId}/stamp_by_coordinates`, 
    { 
      ra, 
      dec, 
      size,
      filter,
      format,
      size_unit: sizeUnit,
      zmin,
      zmax,
      pattern
    },
    { responseType: format === "fits" ? 'blob' : 'arraybuffer' }
  );
  
  return processStampResponse(response, format);
};

export const createBatchStamps = async (fileId, csvFile, sizeUnit = 'arcmin') => {
  // Create batch stamps from CSV file
  const formData = new FormData();
  formData.append('csv_file', csvFile);
  formData.append('size_unit', sizeUnit);

  const response = await apiClient.post(
    `/adss/v1/images/files/${fileId}/batch_stamps`,
    formData,
    {
      responseType: 'blob',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  // Extract summary from headers
  const summary = {
    totalRequested: parseInt(response.headers['x-total-requested'] || '0'),
    successful: parseInt(response.headers['x-successful'] || '0'),
    failed: parseInt(response.headers['x-failed'] || '0'),
  };

  return {
    data: response.data,
    contentType: response.headers['content-type'],
    filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'batch_stamps.tar.gz',
    summary,
    headers: response.headers
  };
};

// Shared function to process RGB response
const processRgbResponse = (response) => {
  // Extract metadata from headers
  const metadataHeader = response.headers['x-metadata'];
  let metadata = {};
  
  // Extract all header fields
  const headerFields = [
    { key: 'x-object-name', destKey: 'objectName' },
    { key: 'x-ra', destKey: 'ra', transform: parseFloat },
    { key: 'x-dec', destKey: 'dec', transform: parseFloat },
    { key: 'x-size', destKey: 'size', transform: parseFloat },
    { key: 'x-size-unit', destKey: 'sizeUnit' },
    { key: 'x-r-filter', destKey: 'rFilter' },
    { key: 'x-g-filter', destKey: 'gFilter' },
    { key: 'x-b-filter', destKey: 'bFilter' },
    { key: 'x-format', destKey: 'format' }
  ];
  
  // Process all header fields
  headerFields.forEach(field => {
    const headerValue = response.headers[field.key];
    if (headerValue && headerValue.trim() !== '') {
      metadata[field.destKey] = field.transform ? field.transform(headerValue) : headerValue;
    }
  });
  
  // Process detailed metadata if present
  if (metadataHeader) {
    try {
      // Convert Python-style values to JavaScript values
      const processedMetadata = metadataHeader
        .replace(/\(/g, '[')
        .replace(/\)/g, ']')
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null');
      
      // Combine with existing metadata
      metadata = { ...JSON.parse(processedMetadata), ...metadata };
    } catch (e) {
      console.error('Error parsing RGB metadata:', e);
    }
  }
  
  // For RGB images, create a Blob with the correct MIME type
  const data = new Blob([response.data], { type: 'image/png' });
  
  return {
    data,
    contentType: response.headers['content-type'],
    filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'rgb.png',
    metadata
  };
};

export const createRgbImage = async (rFileId, gFileId, bFileId, options = {}) => {
  const { ra, dec, size, sizeUnit = 'arcmin', stretch = 3.0, Q = 8.0 } = options;
  
  // Set up request body
  const requestBody = {
    r_file_id: rFileId,
    g_file_id: gFileId,
    b_file_id: bFileId,
    stretch,
    Q,
    format: 'png',
    size_unit: sizeUnit
  };
  
  // Add optional parameters if they exist
  if (ra !== undefined) requestBody.ra = ra;
  if (dec !== undefined) requestBody.dec = dec;
  if (size !== undefined) requestBody.size = size;
  
  // This will return array data for display
  const response = await apiClient.post('/adss/v1/images/rgb', requestBody, 
    { responseType: 'arraybuffer' }
  );
  
  return processRgbResponse(response);
};

export const createRgbByCoordinates = async (collectionId, ra, dec, size, rFilter, gFilter, bFilter, options = {}) => {
  const { sizeUnit = 'arcmin', stretch = 3.0, Q = 8.0, pattern } = options;
  
  // Create RGB image by coordinates
  const response = await apiClient.post(`/adss/v1/images/collections/${collectionId}/rgb_by_coordinates`, 
    { 
      ra, 
      dec, 
      size,
      size_unit: sizeUnit,
      r_filter: rFilter,
      g_filter: gFilter,
      b_filter: bFilter,
      stretch,
      Q,
      format: 'png',
      pattern
    },
    { responseType: 'arraybuffer' }
  );
  
  return processRgbResponse(response);
};

export const createRgbByObject = async (collectionId, objectName, rFilter, gFilter, bFilter, options = {}) => {
  const { size, sizeUnit = 'arcmin', stretch = 3.0, Q = 8.0, pattern } = options;
  
  // Set up request body
  const requestBody = {
    object_name: objectName,
    r_filter: rFilter,
    g_filter: gFilter,
    b_filter: bFilter,
    stretch,
    Q,
    format: 'png',
    size_unit: sizeUnit,
    pattern
  };
  
  // Add optional size if specified
  if (size !== undefined) {
    requestBody.size = size;
  }
  
  // Create RGB image by object name
  const response = await apiClient.post(`/adss/v1/images/collections/${collectionId}/rgb_by_object`, 
    requestBody,
    { responseType: 'arraybuffer' }
  );
  
  return processRgbResponse(response);
};

// Spatial queries
export const coneSearch = async (collectionId, ra, dec, radius, options = {}) => {
  const { filter, limit = 100 } = options;
  
  let url = `/adss/v1/images/collections/${collectionId}/cone_search?ra=${ra}&dec=${dec}&radius=${radius}&limit=${limit}`;
  if (filter) url += `&filter_name=${filter}`;
  
  const response = await apiClient.get(url);
  return response.data;
};

// Permissions
export const getCollectionPermissions = async (collectionId) => {
  const response = await apiClient.get(`/adss/v1/images/collections/${collectionId}/permissions`);
  return response.data;
};

export const addPermission = async (collectionId, roleId, permissionType) => {
  const response = await apiClient.post('/adss/v1/images/permissions', {
    collection_id: collectionId,
    role_id: roleId,
    permission_type: permissionType
  });
  return response.data;
};

export const removePermission = async (permissionId) => {
  const response = await apiClient.delete(`/adss/v1/images/permissions/${permissionId}`);
  return response.data;
};

// Trilogy Image functions
export const createTrilogyImage = async (rFileIds, gFileIds, bFileIds, options = {}) => {
  const { ra, dec, size, sizeUnit = 'arcmin', noiselum = 0.15, satpercent = 15.0, colorsatfac = 2.0 } = options;
  
  // Set up request body
  const requestBody = {
    r_file_ids: rFileIds,
    g_file_ids: gFileIds,
    b_file_ids: bFileIds,
    noiselum,
    satpercent,
    colorsatfac,
    format: 'png',
    size_unit: sizeUnit
  };
  
  // Add optional parameters if they exist
  if (ra !== undefined) requestBody.ra = ra;
  if (dec !== undefined) requestBody.dec = dec;
  if (size !== undefined) requestBody.size = size;
  
  // This will return array data for display
  const response = await apiClient.post('/adss/v1/images/trilogy-rgb', requestBody, 
    { responseType: 'arraybuffer' }
  );
  
  return processRgbResponse(response);
};

export const createTrilogyByCoordinates = async (collectionId, ra, dec, size, rFilters, gFilters, bFilters, options = {}) => {
  const { sizeUnit = 'arcmin', noiselum = 0.15, satpercent = 15.0, colorsatfac = 2.0, pattern } = options;
  
  // Create Trilogy image by coordinates
  const response = await apiClient.post(`/adss/v1/images/collections/${collectionId}/trilogy-rgb_by_coordinates`, 
    { 
      ra, 
      dec, 
      size,
      size_unit: sizeUnit,
      r_filters: rFilters,
      g_filters: gFilters,
      b_filters: bFilters,
      noiselum,
      satpercent,
      colorsatfac,
      format: 'png',
      pattern
    },
    { responseType: 'arraybuffer' }
  );
  
  return processRgbResponse(response);
};

export const createTrilogyByObject = async (collectionId, objectName, rFilters, gFilters, bFilters, options = {}) => {
  const { size, sizeUnit = 'arcmin', noiselum = 0.15, satpercent = 15.0, colorsatfac = 2.0, pattern } = options;
  
  // Set up request body
  const requestBody = {
    object_name: objectName,
    r_filters: rFilters,
    g_filters: gFilters,
    b_filters: bFilters,
    noiselum,
    satpercent,
    colorsatfac,
    format: 'png',
    size_unit: sizeUnit,
    pattern
  };
  
  // Add optional size if specified
  if (size !== undefined) {
    requestBody.size = size;
  }
  
  // Create Trilogy image by object name
  const response = await apiClient.post(`/adss/v1/images/collections/${collectionId}/trilogy-rgb_by_object`, 
    requestBody,
    { responseType: 'arraybuffer' }
  );
  
  return processRgbResponse(response);
};
// Code snippets
export const getCodeSnippet = async (snippetName) => {
  const response = await apiClient.get(`/adss/v1/code-snippets/${snippetName}`);
  return response.data;
};
